/**
 * Partial GraphQL data: how much of a response with field errors may still be
 * shown. A missing or broken attribute of ONE product (a bad SKU, for example)
 * must never break a whole catalog page, while anything that could put a wrong
 * price, stock or link in front of a shopper still fails closed.
 *
 * Every error is put in one of three tiers:
 *
 *   1. Tolerated leaf. The error path ends in an allow-listed display-only leaf
 *      (TOLERATED_LEAF_FIELDS, today only `sku`), the value at exactly that
 *      path is null, and its parent object exists and carries an id. The data
 *      is kept; the field stays null.
 *   2. Dropped item. Only for the listing collections a caller names
 *      (`lists`): any other error inside `<list>.<index>`, or a null list item,
 *      removes that one item. When more than max(1, floor(10%)) of a list's
 *      items would go, the problem is systemic, not bad data, and the whole
 *      response fails. A response without GraphQL errors never fails: its
 *      null items are left out, however many there are.
 *   3. Everything else (no path, a path outside the listed items, a missing
 *      list) fails exactly as before. Transport errors never reach this code.
 *
 * Callers opt in; nothing here runs for a caller that does not ask for it.
 */

/** Display-only leaves whose failure may be shown as a missing value. Never
 * add price, special, stock, url, finalSale, robots or any other fact a
 * shopper or a crawler acts on: that would be fail-open. */
export const TOLERATED_LEAF_FIELDS: readonly string[] = Object.freeze(["sku"]);

/** Share of one list that may be dropped before the response counts as broken. */
export const DROPPED_ITEM_SHARE = 0.1;

export type GraphQLErrorLike = {
  message?: unknown;
  path?: ReadonlyArray<string | number> | null;
};

/** Key path from the response root to a product list, e.g.
 * ["catalogSearchV1", "items"] or ["productsList", "content"]. */
export type GraphQLListPath = ReadonlyArray<string>;

export type GraphQLPartialDataIssue = {
  tier: "tolerated" | "dropped";
  path: string;
  productId: string;
  message: string;
};

export type GraphQLPartialDataEventDetail = GraphQLPartialDataIssue & {
  operation?: string;
};

export type GraphQLPartialDataSummary = {
  issues: GraphQLPartialDataIssue[];
  /** Items removed per list, keyed by the dotted list path. */
  droppedByList: Record<string, number>;
  droppedItems: number;
};

export type GraphQLPartialDataPolicy = {
  /** Tier 2 listing collections. Without lists only tier 1 applies. */
  lists?: ReadonlyArray<GraphQLListPath>;
  /** Called with what was tolerated or dropped, after a usable outcome. */
  onPartialData?: (summary: GraphQLPartialDataSummary) => void;
  /** Short operation label for reports, e.g. "catalogSearchV1". */
  operation?: string;
};

export type GraphQLPartialDataOutcome =
  | ({ ok: true; data: any } & GraphQLPartialDataSummary)
  | {
      ok: false;
      reason: "fatal_error" | "too_many_dropped";
      fatal: unknown[];
    };

const isRecord = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const hasUsableId = (value: Record<string, any>) =>
  value.id !== null && value.id !== undefined && String(value.id).trim() !== "";

const isIndex = (segment: unknown): segment is number =>
  typeof segment === "number" && Number.isInteger(segment) && segment >= 0;

const pathOf = (error: unknown): Array<string | number> | null => {
  const path = (error as GraphQLErrorLike | null)?.path;
  if (!Array.isArray(path) || path.length === 0) return null;
  return path.every(
    (segment) => typeof segment === "string" || isIndex(segment)
  )
    ? [...path]
    : null;
};

const valueAt = (root: unknown, path: ReadonlyArray<string | number>) => {
  let current: any = root;
  for (const segment of path) {
    if (typeof segment === "number") {
      if (!Array.isArray(current) || segment >= current.length)
        return undefined;
    } else if (!isRecord(current)) {
      return undefined;
    }
    current = current[segment as any];
  }
  return current;
};

const BOUNDED_TEXT = /[\u0000-\u001f\u007f]+/g;

const boundedText = (value: unknown, maximumLength: number) =>
  String(value ?? "")
    .replace(BOUNDED_TEXT, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maximumLength);

const productIdOf = (item: unknown) =>
  isRecord(item) && hasUsableId(item)
    ? boundedText(item.id, 32).replace(/[^\w.-]/g, "")
    : "";

const dottedPath = (path: ReadonlyArray<string | number>) =>
  boundedText(path.join("."), 200);

const errorMessage = (error: unknown) =>
  boundedText((error as GraphQLErrorLike | null)?.message, 160);

/**
 * Tier 1 test for one error: an allow-listed leaf, null in the data, under an
 * object that exists and has an id.
 */
export const isToleratedLeafError = (error: unknown, data: unknown) => {
  const path = pathOf(error);
  if (!path || path.length < 2) return false;
  const leaf = path[path.length - 1];
  if (typeof leaf !== "string" || !TOLERATED_LEAF_FIELDS.includes(leaf)) {
    return false;
  }
  const parent = valueAt(data, path.slice(0, -1));
  return (
    isRecord(parent) &&
    hasUsableId(parent) &&
    Object.prototype.hasOwnProperty.call(parent, leaf) &&
    parent[leaf] === null
  );
};

/** Largest number of items one list may lose to tier 2. */
export const maxDroppedItems = (itemsOnPage: number) =>
  Math.max(1, Math.floor(Math.max(0, itemsOnPage) * DROPPED_ITEM_SHARE));

const withListReplaced = (
  root: Record<string, any>,
  listPath: GraphQLListPath,
  items: unknown[]
): Record<string, any> => {
  const [head, ...rest] = listPath;
  return {
    ...root,
    [head]: rest.length ? withListReplaced(root[head], rest, items) : items,
  };
};

/**
 * Classifies a response's GraphQL errors. Returns the data to show (with
 * dropped items removed) or the reason the response must fail as before.
 */
export const classifyGraphQLPartialData = (
  errors: ReadonlyArray<unknown> | null | undefined,
  data: unknown,
  policy: Pick<GraphQLPartialDataPolicy, "lists"> = {}
): GraphQLPartialDataOutcome => {
  const allErrors = Array.isArray(errors) ? [...errors] : [];
  // One entry per list path: a path named twice would filter the already
  // filtered list again by the original indices and drop a healthy item.
  const seenLists = new Set<string>();
  const lists = (policy.lists || []).filter((list) => {
    if (
      !Array.isArray(list) ||
      list.length === 0 ||
      !list.every((segment) => typeof segment === "string" && segment)
    ) {
      return false;
    }
    const key = list.join(".");
    if (seenLists.has(key)) return false;
    seenLists.add(key);
    return true;
  });

  const tolerated: Array<{ error: unknown; path: Array<string | number> }> = [];
  const dropped = new Map<string, Map<number, string>>();
  const fatal: unknown[] = [];

  const markDropped = (
    list: GraphQLListPath,
    index: number,
    message: string
  ) => {
    const key = list.join(".");
    if (!dropped.has(key)) dropped.set(key, new Map());
    const items = dropped.get(key)!;
    if (!items.has(index)) items.set(index, message);
  };

  for (const error of allErrors) {
    if (isToleratedLeafError(error, data)) {
      tolerated.push({ error, path: pathOf(error)! });
      continue;
    }
    const path = pathOf(error);
    const list = path
      ? lists.find(
          (candidate) =>
            path.length > candidate.length &&
            candidate.every((segment, position) => path[position] === segment)
        )
      : undefined;
    const index = list ? path![list.length] : undefined;
    const items = list ? valueAt(data, list) : undefined;
    if (
      list &&
      isIndex(index) &&
      Array.isArray(items) &&
      index < items.length
    ) {
      markDropped(list, index, errorMessage(error));
      continue;
    }
    fatal.push(error);
  }

  if (fatal.length) return { ok: false, reason: "fatal_error", fatal };

  // A null list item is unusable whether or not an error came with it.
  for (const list of lists) {
    const items = valueAt(data, list);
    if (!Array.isArray(items)) continue;
    items.forEach((item, index) => {
      if (!isRecord(item)) markDropped(list, index, "null list item");
    });
  }

  let nextData: any = data;
  const issues: GraphQLPartialDataIssue[] = [];
  const droppedByList: Record<string, number> = {};
  let droppedItems = 0;

  for (const list of lists) {
    const key = list.join(".");
    const removed = dropped.get(key);
    if (!removed?.size) continue;
    const items = valueAt(nextData, list) as unknown[];
    // The cap tells a systemic failure from bad data, so it needs errors to
    // judge. A response without GraphQL errors never fails: its null items
    // are left out and the rest is shown (the same on every caller's path).
    if (allErrors.length > 0 && removed.size > maxDroppedItems(items.length)) {
      return { ok: false, reason: "too_many_dropped", fatal: allErrors };
    }
    for (const [index, message] of [...removed.entries()].sort(
      ([left], [right]) => left - right
    )) {
      issues.push({
        tier: "dropped",
        path: dottedPath([...list, index]),
        productId: productIdOf(items[index]),
        message,
      });
    }
    nextData = withListReplaced(
      nextData,
      list,
      items.filter((_, index) => !removed.has(index))
    );
    droppedByList[key] = removed.size;
    droppedItems += removed.size;
  }

  for (const { error, path } of tolerated) {
    const insideDroppedItem = lists.some((list) => {
      const index = path[list.length];
      return (
        path.length > list.length &&
        list.every((segment, position) => path[position] === segment) &&
        isIndex(index) &&
        !!dropped.get(list.join("."))?.has(index)
      );
    });
    if (insideDroppedItem) continue;
    issues.push({
      tier: "tolerated",
      path: dottedPath(path),
      productId: productIdOf(valueAt(data, path.slice(0, -1))),
      message: errorMessage(error),
    });
  }

  return { ok: true, data: nextData, issues, droppedByList, droppedItems };
};

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

export const PARTIAL_DATA_EVENT = "vf-graphql-partial-data";
const MAX_EVENTS_PER_RESPONSE = 20;
const SSR_REPEAT_WINDOW_MS = 10 * 60 * 1000;
const SSR_BURST_WINDOW_MS = 60 * 1000;
const SSR_MAX_LINES_PER_BURST = 30;
const SSR_MAX_REMEMBERED = 500;

type ReporterEnvironment = {
  now?: () => number;
  warn?: (...args: unknown[]) => void;
  dispatch?: ((detail: GraphQLPartialDataEventDetail) => void) | null;
};

/**
 * Builds a reporter. Server renders log one structured, rate-limited line per
 * product and path (the client build strips console; the SSR build keeps it).
 * Browsers dispatch a `vf-graphql-partial-data` CustomEvent per issue, which
 * tests observe. Nothing is sent to a third party.
 */
export const createGraphQLPartialDataReporter = (
  environment: ReporterEnvironment = {}
) => {
  const now = environment.now || (() => Date.now());
  const warn =
    environment.warn ||
    // eslint-disable-next-line no-console
    ((...args: unknown[]) => console.warn(...args));
  const lastLogged = new Map<string, number>();
  let burstStart = 0;
  let burstLines = 0;

  const browserDispatch =
    environment.dispatch !== undefined
      ? environment.dispatch
      : typeof window !== "undefined" &&
        typeof window.dispatchEvent === "function" &&
        typeof CustomEvent === "function"
      ? (detail: GraphQLPartialDataEventDetail) =>
          window.dispatchEvent(new CustomEvent(PARTIAL_DATA_EVENT, { detail }))
      : null;

  return (issues: GraphQLPartialDataIssue[], operation = "") => {
    if (!Array.isArray(issues) || issues.length === 0) return;
    const label = boundedText(operation, 64);

    if (browserDispatch) {
      for (const issue of issues.slice(0, MAX_EVENTS_PER_RESPONSE)) {
        try {
          browserDispatch({ ...issue, ...(label ? { operation: label } : {}) });
        } catch {
          // Reporting never breaks the page.
        }
      }
      return;
    }

    const time = now();
    if (time - burstStart >= SSR_BURST_WINDOW_MS) {
      burstStart = time;
      burstLines = 0;
    }
    for (const issue of issues) {
      const key = `${issue.tier}|${issue.path.replace(
        /\.\d+(?=\.|$)/g,
        ".*"
      )}|${issue.productId}`;
      const previous = lastLogged.get(key);
      if (previous !== undefined && time - previous < SSR_REPEAT_WINDOW_MS) {
        continue;
      }
      if (burstLines >= SSR_MAX_LINES_PER_BURST) return;
      burstLines += 1;
      lastLogged.delete(key);
      lastLogged.set(key, time);
      while (lastLogged.size > SSR_MAX_REMEMBERED) {
        const oldest = lastLogged.keys().next().value;
        if (oldest === undefined) break;
        lastLogged.delete(oldest);
      }
      warn(
        "[graphql-partial-data]",
        JSON.stringify({
          tier: issue.tier,
          path: issue.path,
          productId: issue.productId,
          message: issue.message,
          ...(label ? { operation: label } : {}),
        })
      );
    }
  };
};

let defaultReporter: ReturnType<
  typeof createGraphQLPartialDataReporter
> | null = null;

/** Reports tolerated or dropped errors (see createGraphQLPartialDataReporter). */
export const reportGraphQLPartialData = (
  issues: GraphQLPartialDataIssue[],
  operation = ""
) => {
  if (!defaultReporter) defaultReporter = createGraphQLPartialDataReporter();
  defaultReporter(issues, operation);
};

export class GraphQLPartialDataError extends Error {
  graphQLErrors: unknown[];

  constructor(errors: unknown[]) {
    super(
      errors
        .map((item: any) => String(item?.message || item || ""))
        .filter(Boolean)
        .join("; ") || "GraphQL query failed"
    );
    this.name = "GraphQLPartialDataError";
    this.graphQLErrors = errors;
  }
}

/**
 * For callers that run `$vfapollo` directly with errorPolicy "all": returns
 * the data to use, reporting what was tolerated or dropped, or throws a
 * GraphQLPartialDataError carrying every error when the response must fail.
 */
export const acceptGraphQLPartialData = (
  response: { data?: unknown; errors?: ReadonlyArray<unknown> | null } | null,
  policy: GraphQLPartialDataPolicy = {}
) => {
  const errors = Array.isArray(response?.errors) ? response!.errors! : [];
  const outcome = classifyGraphQLPartialData(
    errors,
    response?.data ?? null,
    policy
  );
  if (!outcome.ok) throw new GraphQLPartialDataError([...errors]);
  reportGraphQLPartialData(outcome.issues, policy.operation);
  if (outcome.issues.length || outcome.droppedItems) {
    policy.onPartialData?.({
      issues: outcome.issues,
      droppedByList: outcome.droppedByList,
      droppedItems: outcome.droppedItems,
    });
  }
  return outcome.data;
};
